import { Fragment } from 'react';
import styled from 'styled-components';
import { Component, createContext } from 'react';
import { arrayOf, shape, string, object } from 'prop-types';
import { api } from '../lib/api';
import Router, { withRouter } from 'next/router';
import { compose } from 'recompose';
import { withI18next } from '../lib/withI18next';
import getCurrentLang from '../lib/getCurrentLang';
import '@fortawesome/fontawesome/styles.css';
import 'react-select/dist/react-select.css';

import { LAPTOP_SIZE, QUERY_PARAMS } from '../constants';

import GameCards from '../components/GameCards';
import IntroImageWithText from '../components/shared/IntroImageWithText';
import Layout from '../components/Layout';
import GamesFilter from '../components/GamesFilter/';
import ErrorBoundary from '../components/shared/ErrorBoundary';

const NUMBER_OF_GAMES_DOWNLOADED = 12;
export const HeaderMenuContext = createContext();

const initialFetch = lang =>
  api.get('/dameHruSQLAPI', {
    params: {
      isInitial: true,
      from: 0,
      limit: NUMBER_OF_GAMES_DOWNLOADED,
      lang: lang,
    },
  });

const GameFilterContainer = styled.div`
  @media (max-width: ${LAPTOP_SIZE}) {
    display: none;
  }
`;

const defaultState = categories => ({
  title: '',
  age: {
    min: 3,
    max: 21,
  },
  players: {
    min: 1,
    max: 10,
  },
  selectedCategories: [],
  games: [],
  showLoadMoreButton: true,
  numberOfGamesShown: 12,
  searchString: '',
  sectionTitle: '',
  isCacheDirty: false,
  categories: categories,
  lang: 'cs',
});

class Home extends Component {
  state = defaultState(this.props.data.categories);

  componentDidMount() {
    const { query } = this.props.router;

    if (Object.keys(query).length !== 0) {
      this.updateFromUrl();
    }

    Router.onRouteChangeComplete = () => {
      this.updateFromUrl();
    };

    this.setState({ games: this.props.data.games });
  }

  updateFromUrl = () => {
    const { query } = this.props.router;
    let categoryArray = [];

    if (
      Object.keys(query).some(queryParam => QUERY_PARAMS.includes(queryParam))
    ) {
      if (query.categories !== '') {
        categoryArray = query.categories.split(',');
      }
      this.setState({
        searchString: query.search,
        players: {
          min: Number(query.minPlayers),
          max: Number(query.maxPlayers),
        },
        age: {
          min: Number(query.minAge),
          max: Number(query.maxAge),
        },
        selectedCategories: categoryArray,
      });
      this.updateGames(true);
    } else {
      this.setState({
        age: {
          min: 3,
          max: 21,
        },
        players: {
          min: 1,
          max: 10,
        },
        selectedCategories: [],
        searchString: '',
      });
      this.updateGames(true);
    }
  };

  updateQuery = () => {
    const { age, players, selectedCategories, searchString } = this.state;
    const lng = getCurrentLang(this.props.i18n).value;

    Router.push({
      pathname: '/',
      query: {
        lng,
        search: searchString,
        categories: selectedCategories.toString(),
        minAge: age.min,
        maxAge: age.max,
        minPlayers: players.min,
        maxPlayers: players.max,
      },
    });
  };

  updateFilterState = (filterName, value) => {
    this.setState({
      [filterName]: { ...this.state[filterName], min: value[0], max: value[1] },
    });
  };

  updateCategory = category => {
    const { selectedCategories } = this.state;
    if (selectedCategories.includes(category)) {
      this.setState(
        {
          selectedCategories: selectedCategories.filter(
            item => item !== category
          ),
        },
        () => this.updateQuery()
      );
    } else {
      this.setState(
        {
          selectedCategories: [category, ...selectedCategories],
        },
        () => this.updateQuery()
      );
    }
  };

  updateI18lCategoryList = async () => {
    const res = await initialFetch(getCurrentLang(this.props.i18n).value);
    this.setState({
      categories: res.data.categories,
      games: res.data.games,
    });
  };

  updateSearch = searchString => {
    this.setState({
      searchString,
    });
  };

  updateGames = async isCacheDirty => {
    if (isCacheDirty) {
      await this.setState({
        numberOfGamesShown: 0,
      });
    }
    const res = await this.fetchResults();

    this.setState(prevState => {
      return {
        isCacheDirty: isCacheDirty,
        sectionTitle: prevState.searchString,
        games: isCacheDirty ? res.data : [...this.state.games, ...res.data],
        numberOfGamesShown:
          prevState.numberOfGamesShown + NUMBER_OF_GAMES_DOWNLOADED,
        showLoadMoreButton: !(res.data.length < NUMBER_OF_GAMES_DOWNLOADED),
        searchString: '',
      };
    });
  };

  fetchResults = () => {
    const {
      title,
      age,
      players,
      selectedCategories,
      numberOfGamesShown,
      searchString,
    } = this.state;
    const categories = selectedCategories.sort((a, b) => a - b).join();
    const lang = getCurrentLang(this.props.i18n).value;
    const normalizedSearchString = searchString
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    return api.get('/dameHruSQLAPI', {
      params: {
        title,
        min_age: age.min,
        max_age: age.max,
        min_players: players.min,
        max_players: players.max,
        categories,
        searchString: normalizedSearchString,
        from: numberOfGamesShown,
        limit: NUMBER_OF_GAMES_DOWNLOADED,
        lang,
      },
    });
  };

  resetState = () =>
    this.setState(
      defaultState(this.props.data.categories),
      this.updateI18lCategoryList
    );

  render() {
    const {
      games,
      age,
      players,
      selectedCategories,
      isCacheDirty,
      categories,
    } = this.state;

    const renderGameFilter = (
      <GamesFilter
        age={age}
        players={players}
        selectedCategories={selectedCategories}
        categories={categories}
        updateQuery={this.updateQuery}
        updateCategory={this.updateCategory}
        updateFilterState={this.updateFilterState}
      />
    );

    return (
      <HeaderMenuContext.Provider
        value={{
          searchString: this.state.searchString,
          renderGameFilter,
        }}
      >
        <Layout
          resetState={this.resetState}
          hasTransparentHeader
          updateQuery={this.updateQuery}
          updateSearch={this.updateSearch}
          showSearchBar={true}
          updateI18lCategoryList={this.updateI18lCategoryList}
        >
          <IntroImageWithText
            height="300px"
            bgImage="url(/static/intro_background_image.png)"
          />
          <Fragment>
            <GameFilterContainer>
              <ErrorBoundary>{renderGameFilter}</ErrorBoundary>
            </GameFilterContainer>
            <ErrorBoundary>
              <div id="CardsContainer">
                <GameCards
                  games={games}
                  showLoadMoreButton={this.state.showLoadMoreButton}
                  updateGames={this.updateGames}
                  sectionTitle={this.state.sectionTitle}
                  isCacheDirty={isCacheDirty}
                />
              </div>
            </ErrorBoundary>
          </Fragment>
        </Layout>
      </HeaderMenuContext.Provider>
    );
  }
}

Home.getInitialProps = async ({ req }) => {
  const res = await initialFetch(
    req ? (req.language ? req.language.substring(0, 2) : 'cs') : 'cs'
  );
  return { data: res.data };
};

Home.propTypes = {
  data: shape({
    categories: arrayOf(
      shape({
        name: string.isRequired,
        id: string.isRequired,
      }).isRequired
    ),
  }),
  router: object,
};

export default compose(
  withRouter,
  withI18next()
)(Home);



// WEBPACK FOOTER //
// pages/index.js